"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GoogleShoppingClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const commons_logs_1 = require("@deliverr/commons-logs");
const GoogleShoppingError_1 = require("./GoogleShoppingError");
const GOOGLE_SHOPPING_URL = process.env.GOOGLE_SHOPPING_URL;
const GOOGLE_SHOPPING_BASIC_AUTH_USER = process.env.GOOGLE_SHOPPING_BASIC_AUTH_USER;
const GOOGLE_SHOPPING_BASIC_AUTH_PASSWORD = process.env.GOOGLE_SHOPPING_BASIC_AUTH_PASSWORD;
class GoogleShoppingClient extends base_client_1.ApiBasicAuthClient {
    constructor(url = GOOGLE_SHOPPING_URL, user = GOOGLE_SHOPPING_BASIC_AUTH_USER, password = GOOGLE_SHOPPING_BASIC_AUTH_PASSWORD) {
        super({
            baseURL: `${url}/v1/`,
            basicAuthUser: user,
            basicAuthPassword: password,
        });
        if (!user || !password) {
            commons_logs_1.log.warn("Missing GOOGLE_SHOPPING_BASIC_AUTH_USER and GOOGLE_SHOPPING_BASIC_AUTH_PASSWORD environment variables");
        }
    }
    async getSeller(sellerId) {
        try {
            return await this.get(`/seller/${sellerId}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [GoogleShoppingError_1.GoogleShoppingError.SELLER_NOT_FOUND]);
        }
    }
    async getAllSellers(limit, offset, filters = {}) {
        try {
            return await this.get(`/sellers`, Object.assign({ limit,
                offset }, filters));
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async saveOrUpdateSeller(seller) {
        try {
            return await this.put("/seller", seller);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                GoogleShoppingError_1.GoogleShoppingError.DUPLICATE_MERCHANT_ID,
                GoogleShoppingError_1.GoogleShoppingError.NOT_AN_ADMIN,
                GoogleShoppingError_1.GoogleShoppingError.UNAUTHORIZED,
                GoogleShoppingError_1.GoogleShoppingError.UNKNOWN_GOOGLE_API_CLIENT_ERROR,
            ]);
        }
    }
    async updateSeller(sellerId, update) {
        try {
            return await this.put(`/seller/${sellerId}`, update);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                GoogleShoppingError_1.GoogleShoppingError.DUPLICATE_MERCHANT_ID,
                GoogleShoppingError_1.GoogleShoppingError.INVALID_FEED_ID,
                GoogleShoppingError_1.GoogleShoppingError.NOT_AN_ADMIN,
                GoogleShoppingError_1.GoogleShoppingError.SELLER_NOT_FOUND,
                GoogleShoppingError_1.GoogleShoppingError.UNAUTHORIZED,
            ]);
        }
    }
    async refreshSeller(sellerId) {
        try {
            return await this.post(`/refresh/seller/${sellerId}`, undefined);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                GoogleShoppingError_1.GoogleShoppingError.INVALID_FEED_ID,
                GoogleShoppingError_1.GoogleShoppingError.NO_SELLER_FAST_TAGS,
                GoogleShoppingError_1.GoogleShoppingError.NOT_AN_ADMIN,
                GoogleShoppingError_1.GoogleShoppingError.REFRESH_ALREADY_IN_PROGRESS,
                GoogleShoppingError_1.GoogleShoppingError.SELLER_NOT_FOUND,
                GoogleShoppingError_1.GoogleShoppingError.UNAUTHORIZED,
            ]);
        }
    }
    async isSellerEligibleForShoppingActions(sellerId) {
        try {
            const { isEligibleForShoppingActions } = await this.get(`/seller/${sellerId}/shopping-actions-eligibility`);
            return isEligibleForShoppingActions;
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async updateLinkForDsku(sellerId, dsku, listingUrl, productIdentifier) {
        try {
            return await this.put(`/products/seller/${sellerId}/dsku/${dsku}/product`, {
                offerId: productIdentifier,
                listingUrl,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [GoogleShoppingError_1.GoogleShoppingError.PRODUCT_NOT_FOUND, GoogleShoppingError_1.GoogleShoppingError.INVALID_URL]);
        }
    }
    async mapOrderDskus(sellerId, googleOrderId, orderItems) {
        try {
            return await this.put(`/seller/${sellerId}/order/${googleOrderId}/products`, orderItems);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async activateSeller(sellerId) {
        try {
            return await this.post(`/seller/${sellerId}/activate`, undefined);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                GoogleShoppingError_1.GoogleShoppingError.SELLER_NOT_FOUND,
                GoogleShoppingError_1.GoogleShoppingError.NOT_AN_ADMIN,
                GoogleShoppingError_1.GoogleShoppingError.UNAUTHORIZED,
            ]);
        }
    }
    async getOrderDeliveryDetails(sellerId, googleOrderId) {
        try {
            const { promisedDeliveryTime: promisedDeliveryTimeStr, fastTagStatus } = await this.get(`/seller/${sellerId}/order/${googleOrderId}/delivery`);
            const promisedDeliveryTime = promisedDeliveryTimeStr ? new Date(promisedDeliveryTimeStr) : undefined;
            return { promisedDeliveryTime, fastTagStatus };
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async sendOrderTrackingSignal(sellerId, deliverrOrderId) {
        try {
            return this.post(`/seller/${sellerId}/order/${deliverrOrderId}/order-tracking-signal`, undefined);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [GoogleShoppingError_1.GoogleShoppingError.ORDER_NOT_FOUND, GoogleShoppingError_1.GoogleShoppingError.SELLER_NOT_FOUND]);
        }
    }
    async deactivateSeller(sellerId) {
        try {
            return await this.delete(`/seller/${sellerId}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [GoogleShoppingError_1.GoogleShoppingError.SELLER_NOT_FOUND]);
        }
    }
    async getLinkForDsku(sellerId, dsku) {
        try {
            return await this.get(`/products/seller/${sellerId}/dsku/${dsku}/link`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                GoogleShoppingError_1.GoogleShoppingError.SELLER_NOT_FOUND,
                GoogleShoppingError_1.GoogleShoppingError.MISSING_MERCHANT_ID,
                GoogleShoppingError_1.GoogleShoppingError.NOT_FOUND_IN_MARKETPLACE,
            ]);
        }
    }
    async isFastTagEnabled(sellerId, dsku) {
        try {
            const { isActive } = await this.get(`/products/seller/${sellerId}/dsku/${dsku}/active`);
            return isActive;
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async enableFastTag(sellerId, dsku) {
        try {
            await this.put(`/products/seller/${sellerId}/fast-tag/enabled`, [dsku]);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async disableFastTag(sellerId, dsku) {
        try {
            await this.put(`/products/seller/${sellerId}/fast-tag/disabled`, [dsku]);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [GoogleShoppingError_1.GoogleShoppingError.SELLER_NOT_FOUND]);
        }
    }
    async getDskuStateCoverage(dsku) {
        try {
            return await this.get(`/availability/dsku/${dsku}/coverage/twoday`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getStateCoverageByServiceLevel(dsku) {
        try {
            return await this.get(`/availability/servicelevel/${dsku}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getServiceAccountEmail(merchantId) {
        try {
            return await this.get("service-account-email", { merchantId });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
}
exports.GoogleShoppingClient = GoogleShoppingClient;
//# sourceMappingURL=GoogleShoppingClient.js.map